/**
 * useDecisions — the React binding for the Decision Log.
 *
 * Strict layering: the page calls THIS hook; the hook calls {@link DecisionStore}
 * (and the existing Goal / Workstream / Task stores plus the sibling Service /
 * TeamDocument stores) to resolve linked-entity titles CLIENT-SIDE for the cards
 * + pickers; the stores call the api facade. The page never imports a store or
 * the api directly.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { GoalStore } from '../goals/goalStore';
import { WorkstreamStore } from '../workstreams/workstreamStore';
import { TaskStore } from '../tasks/taskStore';
import { ServiceStore } from '../services/serviceStore';
import { TeamDocumentStore } from '../documents/documentStore';
import { DecisionStore } from './decisionStore';
import { sortDecisions } from './decisionModel';
import type { Decision, DecisionDraft, DecisionSummary } from './types';
import type { GoalSummary } from '../goals/types';
import type { WorkstreamSummary } from '../workstreams/types';
import type { Task } from '../tasks/types';
import type { ServiceSummary } from '../services/types';
import type { TeamDocumentSummary } from '../documents/types';

export interface UseDecisionsResult {
  decisions: DecisionSummary[];
  goals: GoalSummary[];
  workstreams: WorkstreamSummary[];
  tasks: Task[];
  services: ServiceSummary[];
  documents: TeamDocumentSummary[];
  currentAlias: string;
  goalTitles: Map<string, string>;
  workstreamTitles: Map<string, string>;
  storyTitles: Map<string, string>;
  serviceTitles: Map<string, string>;
  documentTitles: Map<string, string>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  get: (id: string) => Promise<Decision | undefined>;
  create: (draft: DecisionDraft) => Promise<Decision>;
  update: (id: string, draft: DecisionDraft) => Promise<Decision | undefined>;
  remove: (id: string) => Promise<void>;
}

export function useDecisions(): UseDecisionsResult {
  const api = useApi();
  const stores = useMemo(
    () => ({
      decisions: new DecisionStore(api),
      goals: new GoalStore(api),
      workstreams: new WorkstreamStore(api),
      tasks: new TaskStore(api),
      services: new ServiceStore(api),
      documents: new TeamDocumentStore(api),
    }),
    [api],
  );

  const [decisions, setDecisions] = useState<DecisionSummary[]>([]);
  const [goals, setGoals] = useState<GoalSummary[]>([]);
  const [workstreams, setWorkstreams] = useState<WorkstreamSummary[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [services, setServices] = useState<ServiceSummary[]>([]);
  const [documents, setDocuments] = useState<TeamDocumentSummary[]>([]);
  const [currentAlias, setCurrentAlias] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [decisionRows, goalRows, wsRows, allTasks, serviceRows, docRows, identity] =
        await Promise.all([
          stores.decisions.listSummaries(),
          stores.goals.listSummaries(),
          stores.workstreams.listSummaries(),
          stores.tasks.listAll(),
          stores.services.listSummaries(),
          stores.documents.listSummaries(),
          api.identityProvider.getIdentity(),
        ]);
      setDecisions(sortDecisions(decisionRows));
      setGoals(goalRows);
      setWorkstreams(wsRows);
      setTasks(allTasks);
      setServices(serviceRows);
      setDocuments(docRows);
      setCurrentAlias(identity.alias);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load decisions.');
    } finally {
      setLoading(false);
    }
  }, [stores, api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const goalTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of goals) m.set(g.id, g.title);
    return m;
  }, [goals]);

  const workstreamTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of workstreams) m.set(w.id, w.title);
    return m;
  }, [workstreams]);

  const storyTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tasks) m.set(t.id, t.title);
    return m;
  }, [tasks]);

  const serviceTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of services) m.set(s.id, s.name);
    return m;
  }, [services]);

  const documentTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of documents) m.set(d.id, d.title);
    return m;
  }, [documents]);

  const get = useCallback((id: string) => stores.decisions.get(id), [stores]);

  const create = useCallback(
    async (draft: DecisionDraft) => {
      const saved = await stores.decisions.create(draft);
      await refresh();
      return saved;
    },
    [stores, refresh],
  );

  const update = useCallback(
    async (id: string, draft: DecisionDraft) => {
      const existing = await stores.decisions.get(id);
      if (!existing) return undefined;
      const saved = await stores.decisions.applyDraft(existing, draft);
      await refresh();
      return saved;
    },
    [stores, refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await stores.decisions.softDelete(id);
      await refresh();
    },
    [stores, refresh],
  );

  return {
    decisions,
    goals,
    workstreams,
    tasks,
    services,
    documents,
    currentAlias,
    goalTitles,
    workstreamTitles,
    storyTitles,
    serviceTitles,
    documentTitles,
    loading,
    error,
    refresh,
    get,
    create,
    update,
    remove,
  };
}
